// Methods not using "this" are implementing the interface and cannot be static
/* eslint-disable class-methods-use-this */

import { AxiosResponse } from 'axios';
import { ClosedAccountSettings } from './ClosedAccountSettings';
import { AccountClosedByStatuses } from './AccountClosedByStatuses';
import VersionedResource from '../VersionedResource';
import { StatusSettingsResource } from './StatusSettingsResource';
import { StatusSettings } from './StatusSettings';

export class ClosedAccountSettingsResource extends VersionedResource implements StatusSettingsResource {
  public async getOne(fiId: string): Promise<ClosedAccountSettings> {
    const response: AxiosResponse = await this.apiClient.get(`/api/rcx/config/${fiId}/closed-account-settings`);
    const settings: ClosedAccountSettings = response.data.data;
    return settings;
  }

  public async save(fiId: string, settings: ClosedAccountSettings): Promise<ClosedAccountSettings> {
    const url = `/api/rcx/config/${fiId}/closed-account-settings/`;

    const toSave: any = { };
    Object.assign(toSave, settings);

    // Application logic should prevent UNSET value from reaching here, as it's only meaningful on the
    // client side.  However, in case of a logic error, that value should not be passed to the server.
    if (<unknown>settings.accountClosedBy === AccountClosedByStatuses.UNSET) {
      toSave.accountClosedBy = undefined;
    } else if (<unknown>settings.accountClosedBy === AccountClosedByStatuses.CLOSED_DATE) {
      toSave.statusCodes = [];
    }

    const response: AxiosResponse = await this.apiClient.put(url, toSave);

    const savedSetting: ClosedAccountSettings = response.data.data;
    return savedSetting;
  }

  createEmptySettings(): ClosedAccountSettings {
    return {
      accountClosedBy: AccountClosedByStatuses.UNSET,
      statusCodes: [],
      active: true,
    };
  }

  setIndicatorsOnSettings(settings: StatusSettings, values: string[], conditionalOr: boolean | null) {
    const closedSettings = settings as ClosedAccountSettings;
    if (values.length === 2) {
      if (conditionalOr) {
        closedSettings.accountClosedBy = AccountClosedByStatuses.CLOSED_DATE_OR_STATUS_CODE;
      } else {
        closedSettings.accountClosedBy = AccountClosedByStatuses.CLOSED_DATE_AND_STATUS_CODE;
      }
    } else if (values.length === 0) {
      closedSettings.accountClosedBy = AccountClosedByStatuses.UNSET;
    } else if (values[0] === 'CLOSED_DATE') {
      closedSettings.accountClosedBy = AccountClosedByStatuses.CLOSED_DATE;
    } else if (values[0] === 'STATUS_CODE') {
      closedSettings.accountClosedBy = AccountClosedByStatuses.STATUS_CODE;
    }
  }

  getIndicatorsFromSettings(settings: StatusSettings): string[] | null {
    const closedSettings = settings as ClosedAccountSettings;
    let indicators: string[] = [];

    switch (closedSettings.accountClosedBy) {
      case AccountClosedByStatuses.CLOSED_DATE:
        indicators = [AccountClosedByStatuses.CLOSED_DATE];
        break;


      case AccountClosedByStatuses.STATUS_CODE:
        indicators = [AccountClosedByStatuses.STATUS_CODE];
        break;

      case AccountClosedByStatuses.CLOSED_DATE_OR_STATUS_CODE:
      case AccountClosedByStatuses.CLOSED_DATE_AND_STATUS_CODE:
        indicators = [AccountClosedByStatuses.STATUS_CODE, AccountClosedByStatuses.CLOSED_DATE];
        break;

      default:
        break;
    }

    return indicators;
  }

  getConditionalFromSettings(settings: StatusSettings): string | null {
    const closedSettings = settings as ClosedAccountSettings;

    let conditional: string | null = null;
    switch (closedSettings.accountClosedBy) {
      case AccountClosedByStatuses.CLOSED_DATE_OR_STATUS_CODE:
        conditional = 'OR';
        break;

      case AccountClosedByStatuses.CLOSED_DATE_AND_STATUS_CODE:
        conditional = 'AND';
        break;

      default:
        break;
    }

    return conditional;
  }
}

export default ClosedAccountSettingsResource;
